import {useWorkIsland} from "./WorkIslandContext";
import {useState} from "react";
import {Loader, Text} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {dibAppApi} from "../../../dibAppApi/dibAppApi";
import type {SourceInfo} from "../../../types";
import {ExperimentalLink} from "../../../components/ExperimentalLink";
import {resolveDibHref} from "../../../utils/dibUrls";
import {AccessContext, type AccessEntry} from "../../content/AccessContext";
import {HTMLResource} from "../../content/HTMLResource";
import {ResourceError} from "../../content/ResourceError";
import {ResourceType} from "../../content/types";
import {PanelProvider} from "../../content/panel/PanelContext";
import {SliderPanel} from "../../content/panel/SliderPanel";
import {targetFromSource} from "../citation-preview/useCitationParts";
import {useTopicOpenAnalytics} from "../../content/useTopicOpenAnalytics";
import {noteIslandToPage} from "../../content/topicEntryNote";
import classes from "./TopicIslandContent.module.css";

// Types with a view of their own on the content page (ContentPage's switch).
// The island only renders HTML, so these link to the page; every other type,
// including legacy ones, renders here.
const pageOnlyTypes = new Set([
    ResourceType.news,
    ResourceType.checklist,
    ResourceType.documentGenerator,
    ResourceType.pdf,
]);

/** Renders a complete topic as island content, inside the thread. */
export function TopicIslandContent({source, compactToc}: {
    source: SourceInfo;
    compactToc: boolean;
}) {
    const {t} = useTranslation();
    const {reportReadingContext, selectPassage} = useWorkIsland();
    const target = targetFromSource(source);
    const resourceId = target?.resourceId;
    const [accessEntry, setAccessEntry] = useState<AccessEntry | null>(null);
    const {currentData: resourceType, isFetching, isError, error} =
        dibAppApi.endpoints.getResourceTypeId.useQuery(
            {topicId: resourceId ?? ""},
            {skip: !resourceId}
        );
    const noAccess = accessEntry?.id === resourceId && !accessEntry?.access;
    useTopicOpenAnalytics({
        topicId: resourceId,
        shown: !!resourceId && accessEntry !== null && accessEntry.id === resourceId && accessEntry.access,
        surface: "island",
        segmentId: target?.segmentId || undefined,
        sectionId: target?.sectionId,
        fallbackSource: "citation",
    });
    const href = resolveDibHref(source.url);

    if (!resourceId || (resourceType && pageOnlyTypes.has(resourceType as ResourceType))) {
        return (
            <div className={classes.status}>
                <Text size="sm">{t("WorkIsland.OpenOnSourcePage")}</Text>
                {href && (
                    <ExperimentalLink href={href} onClick={() => noteIslandToPage(resourceId)}>
                        {t("WorkIsland.OpenFullTopic")}
                    </ExperimentalLink>
                )}
            </div>
        );
    }
    if (isError || noAccess) {
        return <div className={classes.status}><ResourceError error={noAccess ? {status: 403} : error} /></div>;
    }
    if (isFetching) {
        return <div className={classes.status} role="status" aria-label={t("citationPreview.loading")}><Loader size="sm" color="gray" /></div>;
    }
    // The lookup resolved without a type (empty body / null).
    if (!resourceType) {
        return <div className={classes.status}><ResourceError error={{status: 404}} /></div>;
    }

    return (
        <PanelProvider>
            <AccessContext.Provider value={setAccessEntry}>
                <HTMLResource
                    resourceId={resourceId}
                    segmentId={target?.segmentId || undefined}
                    embedded={{compactToc, sectionId: target?.sectionId}}
                    onReadingContextChange={reportReadingContext}
                    onTextSelection={selectPassage}
                />
                <SliderPanel />
            </AccessContext.Provider>
        </PanelProvider>
    );
}
